import React, { useEffect, useMemo, useState } from "react";
import { onValue, ref } from "firebase/database";
import { db } from "../firebase";
import "./css/playerDataForm.css";

// Contraseña sencilla para acceso de administración.
// IMPORTANTE: cambia este valor antes de desplegar.
const ADMIN_PASSWORD = "V9!rQ@L7#sA2mF$K";

// Mapeo de códigos de idioma a nombres completos
const getLanguageName = (code) => {
  const languageMap = {
    es: "Español",
    en: "Inglés",
    fi: "Finlandés",
    bg: "Búlgaro",
    cs: "Checo",
    el: "Griego",
  };
  return languageMap[code?.toLowerCase()] || code || "—";
};

const AdminDashboard = () => {
  const [isAdmin, setIsAdmin] = useState(
    () => localStorage.getItem("greenscape_admin") === "true"
  );
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(null);

  // Verificar que Firebase esté inicializado
  useEffect(() => {
    if (!db) {
      console.error("❌ Firebase Database no está inicializado");
      setError("Error: Firebase Database no está inicializado. Verifica la configuración.");
    } else {
      console.log("✅ Firebase Database inicializado correctamente");
    }
  }, []);

  // Manejo de login muy simple basado en contraseña compartida
  const handleLoginSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      localStorage.setItem("greenscape_admin", "true");
      setIsAdmin(true);
      setLoginError("");
    } else {
      setLoginError("Contraseña incorrecta");
    }
  };

  // Cerrar sesión de admin
  const handleLogout = () => {
    localStorage.removeItem("greenscape_admin");
    setIsAdmin(false);
    setPassword("");
    setRecords([]);
  };

  // Suscripción a playerForms cuando el usuario es admin
  useEffect(() => {
    if (!isAdmin) return;

    setLoading(true);
    const playerFormsRef = ref(db, "playerForms");

    console.log("🔍 AdminDashboard: Intentando conectar a Firebase...");
    console.log("🔍 Ruta de Firebase:", "playerForms");

    const unsubscribe = onValue(
      playerFormsRef,
      (snapshot) => {
        console.log("✅ AdminDashboard: Datos recibidos de Firebase");
        const data = snapshot.val();
        console.log("📊 Datos raw de Firebase:", data);
        
        if (!data) {
          console.warn("⚠️ AdminDashboard: No hay datos en Firebase (null o undefined)");
          setRecords([]);
          setLoading(false);
          return;
        }

        const parsed = Object.entries(data).map(([key, value]) => ({
          firebaseKey: key,
          ...(value || {}),
        }));

        console.log("📋 Registros parseados:", parsed.length, "registros");
        setRecords(parsed);
        setLoading(false);
      },
      (error) => {
        console.error("❌ AdminDashboard: Error al leer de Firebase:", error);
        console.error("❌ Detalles del error:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        setError(`Error al conectar con Firebase: ${error.message || error.code || "Error desconocido"}`);
        setLoading(false);
        setRecords([]);
      }
    );

    return () => {
      console.log("🔌 AdminDashboard: Desconectando de Firebase");
      unsubscribe();
    };
  }, [isAdmin]);

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bDate - aDate;
    });
  }, [records]);

  if (!isAdmin) {
    return (
      <div className="player-data-container">
        <div className="form-intro" style={{ maxWidth: 480, margin: "0 auto" }}>
          <h3>Acceso administración</h3>
          <p>
            Esta sección está reservada para el equipo de administración de GreenEscape.
          </p>
          <form
            className="player-form"
            onSubmit={handleLoginSubmit}
            style={{ marginTop: 24 }}
          >
            <div className="form-group full-width">
              <label htmlFor="admin-password">Contraseña</label>
              <input
                type="password"
                id="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Introduce la contraseña de administración"
                required
              />
            </div>
            {loginError && (
              <p
                style={{
                  color: "#b71c1c",
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                {loginError}
              </p>
            )}
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button
                type="submit"
                className="btn-next active"
                style={{ backgroundColor: "#2e7d32" }}
              >
                Entrar
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="player-data-container">
      <div className="progress-header" style={{ marginBottom: 16 }}>
        <div className="progress-info" style={{ width: "100%" }}>
          <span className="player-counter">
            Panel de administración · Registros de formulario: {records.length}
          </span>
          <button
            type="button"
            onClick={handleLogout}
            className="btn-previous"
            style={{ marginLeft: "auto" }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          maxHeight: "70vh",
          overflow: "auto",
          background: "rgba(255,255,255,0.9)",
          borderRadius: 12,
          padding: 16,
        }}
      >
        {error && (
          <div style={{ 
            background: "#ffebee", 
            color: "#c62828", 
            padding: 12, 
            borderRadius: 8, 
            marginBottom: 16,
            border: "1px solid #ef5350"
          }}>
            <strong>Error:</strong> {error}
            <p style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              Abre la consola del navegador (F12) para más detalles.
            </p>
          </div>
        )}
        {loading ? (
          <p>Cargando registros...</p>
        ) : sortedRecords.length === 0 ? (
          <div>
            <p>No hay registros todavía en playerForms.</p>
            <p style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
              Verifica la consola del navegador (F12) para ver los logs de depuración.
            </p>
          </div>
        ) : (
           <table
             style={{
               width: "100%",
               borderCollapse: "collapse",
               fontSize: 14,
               tableLayout: "fixed",
             }}
           >
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 8 }}>Fecha</th>
                <th style={{ textAlign: "left", padding: 8 }}>Edad</th>
                <th style={{ textAlign: "left", padding: 8 }}>Género</th>
                <th style={{ textAlign: "left", padding: 8 }}>Localidad</th>
                <th style={{ textAlign: "left", padding: 8 }}>Ciudad</th>
                <th style={{ textAlign: "left", padding: 8 }}>País</th>
                <th style={{ textAlign: "left", padding: 8 }}>Dispositivo</th>
                <th style={{ textAlign: "left", padding: 8 }}>Idioma</th>
                <th style={{ textAlign: "left", padding: 8 }}>Personaje</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((r) => (
                <tr key={r.firebaseKey}>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.created_at
                      ? new Date(r.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.edad ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.genero ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.localidad ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.ciudad ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.pais ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {r.tipo_device ?? "—"}
                  </td>
                  <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                    {getLanguageName(r.language)}
                  </td>
                   <td style={{ padding: 8, borderTop: "1px solid #ddd" }}>
                     {r.characterName ?? r.characterId ?? "—"}
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

